/* ============================================================
   performerColorsView — MS-1 (TASK-52194f26): pure view model
   for the customer color picker at performer selection.

   Artem: «возможность пользователю выбрать цвет» — after the
   customer picks a performer, they choose a print color:
   - performer tracks stock for the quote's material → only the
     in-stock colors are offered (availability-filtered, the
     backend already drops remaining <= 0);
   - performer tracks stock but is sold out → hint to ask them;
   - performer does not track stock → the standard palette.

   Pure functions; PerformerList owns the state.
   ============================================================ */

import type { PerformerMatch } from '../types';

/** Standard FDM palette offered when the performer does not track stock. */
export const DEFAULT_COLOR_PALETTE: readonly string[] = [
  'Чёрный', 'Белый', 'Серый', 'Красный', 'Синий', 'Зелёный',
  'Жёлтый', 'Оранжевый', 'Прозрачный', 'Натуральный',
];

export type ColorChoice =
  | { mode: 'palette'; colors: readonly string[] }
  | { mode: 'stock'; colors: readonly string[] }
  | { mode: 'ask'; colors: readonly string[] };

/** Resolve the color picker posture for the selected performer. */
export function colorChoiceFor(match: PerformerMatch | null | undefined): ColorChoice {
  if (!match) return { mode: 'palette', colors: [] };
  if (match.tracksMaterialStock) {
    const colors = (match.availableColors ?? []).filter((c) => typeof c === 'string' && c.length > 0);
    return colors.length > 0
      ? { mode: 'stock', colors }
      : { mode: 'ask', colors: [] };
  }
  return { mode: 'palette', colors: DEFAULT_COLOR_PALETTE };
}

/** Caption under the picker heading for each posture. */
export function colorCaption(choice: ColorChoice): string {
  switch (choice.mode) {
    case 'stock': return 'В наличии у исполнителя';
    case 'ask': return 'Все цвета исполнителя распроданы — уточните цвет в переписке или выберите другого исполнителя';
    case 'palette': return 'Стандартные цвета (уточняется у исполнителя)';
  }
}

/**
 * Keep the selection valid when the performer changes: keep the current color
 * when it is still offered, otherwise take the first offered color ('ask'
 * mode clears the pick — no in-stock colors to default to).
 */
export function resolveSelection(choice: ColorChoice, current: string | null): string | null {
  if (choice.mode === 'ask') return null;
  if (current != null && choice.colors.includes(current)) return current;
  return choice.colors.length > 0 ? choice.colors[0] : null;
}
